// src/pages/public/Raffle.tsx
// Raffle page — info + revealed prize results. EMPLOYEE-ONLY (Entra SSO):
// the raffle is a WIC-employee perk and revealed winners are employee names,
// so it's gated behind sign-in (matches the Gallery / My RSVP treatment).
// Data: /api/me/raffle (auth required; only revealed prizes + drawn winners;
// full config is admin-only). Gated on raffle.enabled — when off, shows a
// "coming soon" state mirroring Schedule.tsx.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";

interface PublicPrize {
  slug: string;
  name: string;
  description?: string;
  sponsor?: string;
  winnerName?: string;
  winnerTicket?: number;
}
interface RaffleView {
  enabled: boolean;
  description?: string;
  ticketPrefix?: string;
  prizes?: PublicPrize[];
  totalPrizes?: number;
  revealedCount?: number;
}

function ticketLabel(n: number, prefix?: string): string {
  const s = String(n).padStart(3, "0");
  return prefix ? `${prefix}-${s}` : `#${s}`;
}

export default function Raffle() {
  const { isSignedIn, authConfigured, signIn, getApiToken } = useAuth();
  const [view, setView] = useState<RaffleView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSignedIn) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getApiToken();
        if (!token) throw new Error("Raffle unavailable");
        const r = await fetch("/api/me/raffle?year=2026", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error("Raffle unavailable");
        const data: RaffleView = await r.json();
        if (!cancelled) { setView(data); setLoading(false); }
      } catch (e: any) {
        if (!cancelled) { setError(e.message ?? "Raffle unavailable"); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, getApiToken]);

  // Sign-in gate — the raffle is employee-only. Prompt rather than bounce home
  // so a shared /raffle link lands somewhere useful.
  if (!isSignedIn) {
    return (
      <section className="section">
        <div className="container disabled-page">
          <span className="eyebrow">Employees Only</span>
          <h1>Sign in to view the raffle</h1>
          <p>The WICnic raffle is for WIC employees. Sign in with your work account to see prizes and winners.</p>
          {authConfigured ? (
            <button className="btn-back" onClick={signIn}>Employee sign-in</button>
          ) : (
            <Link to="/" className="btn-back">Back to home</Link>
          )}
          <style>{`
            .disabled-page { max-width: 640px; padding: 4rem 0; }
            .disabled-page h1 { font-family: var(--font-display); font-weight: 700; text-transform: uppercase; margin: 0.5rem 0 1rem; }
            .disabled-page p { color: var(--steel); margin-bottom: 2rem; font-size: 1.05rem; }
            .eyebrow { font-family: var(--font-display); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); }
            .btn-back {
              display: inline-block; font-family: var(--font-display); font-weight: 700;
              font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase;
              color: var(--white); background: var(--red); padding: 0.85rem 2rem;
              text-decoration: none; border: none; cursor: pointer;
            }
          `}</style>
        </div>
      </section>
    );
  }

  // Disabled / not configured — friendly placeholder (matches Schedule.tsx).
  if (!loading && !error && view && view.enabled === false) {
    return (
      <section className="section">
        <div className="container disabled-page">
          <span className="eyebrow">Coming Soon</span>
          <h1>The raffle isn't live yet</h1>
          <p>Check back closer to the picnic for prizes and winners.</p>
          <Link to="/" className="btn-back">Back to home</Link>
          <style>{`
            .disabled-page { max-width: 640px; padding: 4rem 0; }
            .disabled-page h1 { font-family: var(--font-display); font-weight: 700; text-transform: uppercase; margin: 0.5rem 0 1rem; }
            .disabled-page p { color: var(--steel); margin-bottom: 2rem; font-size: 1.05rem; }
            .eyebrow { font-family: var(--font-display); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); }
            .btn-back {
              display: inline-block; font-family: var(--font-display); font-weight: 700;
              font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase;
              color: var(--white); background: var(--red); padding: 0.85rem 2rem;
              text-decoration: none;
            }
          `}</style>
        </div>
      </section>
    );
  }

  const prizes = view?.prizes ?? [];
  const drawn = prizes.filter(p => typeof p.winnerTicket === "number" || p.winnerName);
  const hasResults = drawn.length > 0;

  return (
    <>
      <style>{`
        .raffle-page { min-height: 100vh; background: var(--bg); }
        .raffle-hero {
          background: var(--black); color: var(--white);
          padding: 5rem 4rem 4rem; position: relative; overflow: hidden;
        }
        .raffle-hero::before {
          content: ''; position: absolute; inset: 0;
          background: repeating-linear-gradient(-45deg, transparent, transparent 12px, rgba(255,255,255,0.02) 12px, rgba(255,255,255,0.02) 24px);
          pointer-events: none;
        }
        .hero-inner { max-width: 1100px; margin: 0 auto; position: relative; }
        .eyebrow {
          font-family: var(--font-display); font-size: 0.72rem; font-weight: 500;
          letter-spacing: 0.35em; text-transform: uppercase; color: var(--red);
          margin-bottom: 0.75rem;
        }
        .raffle-hero h1 {
          font-family: var(--font-display); font-size: clamp(2.4rem, 5vw, 3.6rem);
          font-weight: 700; text-transform: uppercase; line-height: 1; margin: 0;
        }
        .hero-desc {
          font-family: var(--font-body); font-size: 1.05rem; color: rgba(255,255,255,0.75);
          margin-top: 1.5rem; max-width: 60ch; line-height: 1.6;
        }
        .hero-count {
          display: inline-block; margin-top: 1.75rem;
          font-family: var(--font-display); font-size: 0.75rem; font-weight: 600;
          letter-spacing: 0.18em; text-transform: uppercase; color: rgba(255,255,255,0.6);
          border: 1px solid rgba(255,255,255,0.2); padding: 0.4rem 0.9rem;
        }
        .raffle-body { max-width: 1100px; margin: 0 auto; padding: 4rem; }
        @media (max-width: 900px) {
          .raffle-body { padding: 3rem 1.5rem; }
          .raffle-hero { padding: 3.5rem 1.5rem 3rem; }
        }
        .section-eyebrow {
          font-family: var(--font-display); font-size: 0.7rem; font-weight: 500;
          letter-spacing: 0.25em; text-transform: uppercase; color: var(--red);
          margin: 0 0 1.5rem;
        }
        .how-it-works {
          background: var(--white); border: 1px solid var(--border);
          padding: 2rem 2.25rem; margin-bottom: 3rem;
        }
        .how-it-works p {
          font-family: var(--font-body); font-size: 0.98rem; color: var(--steel);
          line-height: 1.6; margin: 0;
        }
        .prize-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1px; background: var(--border); border: 1px solid var(--border);
        }
        .prize-card {
          background: var(--white); padding: 1.75rem;
          display: flex; flex-direction: column; gap: 0.6rem;
        }
        .prize-card.won { border-left: 3px solid var(--red); }
        .prize-name {
          font-family: var(--font-display); font-size: 1.2rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.02em; color: var(--black);
          margin: 0; line-height: 1.15;
        }
        .prize-sponsor {
          font-family: var(--font-display); font-size: 0.65rem; font-weight: 600;
          letter-spacing: 0.15em; text-transform: uppercase; color: var(--muted);
        }
        .prize-desc {
          font-family: var(--font-body); font-size: 0.9rem; color: var(--steel);
          line-height: 1.5; margin: 0;
        }
        .prize-winner {
          margin-top: auto; padding-top: 0.9rem; border-top: 1px solid var(--border);
        }
        .winner-label {
          font-family: var(--font-display); font-size: 0.6rem; font-weight: 600;
          letter-spacing: 0.2em; text-transform: uppercase; color: var(--red);
          display: block; margin-bottom: 0.25rem;
        }
        .winner-name {
          font-family: var(--font-display); font-size: 1.05rem; font-weight: 700;
          text-transform: uppercase; color: var(--black);
        }
        .winner-ticket {
          font-family: var(--font-body); font-size: 0.85rem; color: var(--muted);
          margin-left: 0.4rem;
        }
        .winner-pending {
          font-family: var(--font-body); font-size: 0.85rem; color: var(--muted);
          font-style: italic;
        }
        .state-box { padding: 4rem 2rem; text-align: center; font-family: var(--font-body); color: var(--muted); }
        .state-box h2 { font-family: var(--font-display); font-size: 1.4rem; font-weight: 600; text-transform: uppercase; color: var(--mid); margin-bottom: 0.5rem; }
      `}</style>

      <div className="raffle-page">
        <div className="raffle-hero">
          <div className="hero-inner">
            <p className="eyebrow">Picnic Raffle</p>
            <h1>WICnic Raffle</h1>
            {view?.description && <p className="hero-desc">{view.description}</p>}
            {!loading && typeof view?.totalPrizes === "number" && view.totalPrizes > 0 && (
              <span className="hero-count">
                {drawn.length} of {view.totalPrizes} prize{view.totalPrizes === 1 ? "" : "s"} drawn
              </span>
            )}
          </div>
        </div>

        {loading && <div className="state-box"><h2>Loading raffle…</h2></div>}
        {error && (
          <div className="state-box">
            <h2>Raffle unavailable</h2>
            <p>Check back soon for prizes and winners.</p>
          </div>
        )}

        {!loading && !error && view?.enabled && (
          <div className="raffle-body">
            <div className="how-it-works">
              <p>
                Every WIC employee who RSVPs <strong>yes</strong> is automatically entered with a
                ticket number — no purchase, no sign-up. Winning numbers are drawn live at the
                picnic, and results are posted here as they're announced.
              </p>
            </div>

            <p className="section-eyebrow">{hasResults ? "Winners" : "Prizes"}</p>

            {prizes.length === 0 ? (
              <div className="state-box">
                <h2>Winners announced at the picnic</h2>
                <p>Prizes and results will appear here once the drawing begins.</p>
              </div>
            ) : (
              <div className="prize-grid">
                {prizes.map(p => {
                  const won = typeof p.winnerTicket === "number" || !!p.winnerName;
                  return (
                    <div key={p.slug} className={`prize-card${won ? " won" : ""}`}>
                      {p.sponsor && <span className="prize-sponsor">{p.sponsor}</span>}
                      <h3 className="prize-name">{p.name}</h3>
                      {p.description && <p className="prize-desc">{p.description}</p>}
                      <div className="prize-winner">
                        {won ? (
                          <>
                            <span className="winner-label">Winner</span>
                            <span className="winner-name">{p.winnerName || "Claimed"}</span>
                            {typeof p.winnerTicket === "number" && (
                              <span className="winner-ticket">Ticket {ticketLabel(p.winnerTicket, view?.ticketPrefix)}</span>
                            )}
                          </>
                        ) : (
                          <span className="winner-pending">To be drawn at the picnic</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
